import mdAxios from 'core/common/config/axios-config';
import flatten = require("lodash/flatten");
import MdEntity from 'core/metamodel/mdEntity';
import {LovItem} from "core/metamodel/mdEntity";
import {observable, ObservableMap, action} from "mobx";
import {EntityConfig, ID} from "core/metamodel/metaConstant";
import {ClosedContentProps, lovValue} from "core/form/formUtils";
import {DefinitionType} from "core/form/form";
import {getItemFromLocalStorage, setItemToLocalStorage} from "core/common/utils/storage";
import {loc} from "core/common/config/locales";


type EntitySpec = {entity: string, lov?: boolean};
export type EntityNameOrSpec = EntitySpec | string;


class MdEntityService {

  @observable entityMap: ObservableMap<MdEntity> = observable.shallowMap<MdEntity>(null);

  private ENTITY_REGISTRY: {[key: string]: EntityConfig} = {};
  private FORM_DEFINITION_REGISTRY: {[key: string]: DefinitionType} = {};
  private CLOSED_CONTENT_REGISTRY: {[key: string]: React.ComponentClass<ClosedContentProps<any>>} = {};



  getEntityConfig(entityName): EntityConfig {
    return this.ENTITY_REGISTRY[entityName];
  }


  setEntityConfig(entityConfig: EntityConfig) {
    this.ENTITY_REGISTRY[entityConfig.entityName] = entityConfig;
  }

  getFormDefinition(formName: string): DefinitionType {
    return this.FORM_DEFINITION_REGISTRY[formName];
  }
  setFormDefinition(formName: string, formDefinition: DefinitionType) {
    this.FORM_DEFINITION_REGISTRY[formName] = formDefinition;
  }

  getEntityDetailRoute(entityName: string) : string {
    const entityConfig = this.getEntityConfig(entityName);
    if (!entityConfig) throw new Error("EntityConfig of " + entityName + " is null. Check @entityConfig class annotation on object " + entityName);
    if (!entityConfig.detailroute) throw new Error("detailRoute on EntityConfig object " + entityName + " is not configured");
    return entityConfig.detailroute;
  }


  getClosedContent(formName: string): React.ComponentClass<ClosedContentProps<any>> {
    return this.CLOSED_CONTENT_REGISTRY[formName];
  }
  setClosedContent(formName: string, closedContent:  React.StatelessComponent<ClosedContentProps<any>>|React.ComponentClass<ClosedContentProps<any>>) {
    this.CLOSED_CONTENT_REGISTRY[formName] = closedContent as any;
  }



  getEntity(entityName: string) : MdEntity {
    return this.entityMap.get(entityName);
  }



  /**
   * Dotahne ze serveru entity ze zadanych entityNames, ktere neexistuji v metamodel/entities
   * Pokud je zadane pole withLovs, tak k nim jeste dotahne lovItems, pokud nejsou jeste dotazene (lovitems == undefined)
   * Pokud se neco dotahovalo aktualizuje metamodel/entities
   *  Vrati promise resolvovany do (doplneneho) entityObject
   * @param entityNamesOrSpecs - pole jmen entit [Party, ...] (potom se predpoklada, ze je to without lov) nebo pole objektu [{entity: 'Country', lov: true}, ...]
     */
  @action fetchEntities(entityNamesOrSpecs: EntityNameOrSpec[]): Promise<ObservableMap<MdEntity>> {
    console.log('fetchEntities %O', entityNamesOrSpecs);

    let entitySpecs: EntitySpec[] = entityNamesOrSpecs.filter(v => !!v).map(v => (typeof v === 'string') ? {entity: v} : v);

    this.fetchEntitiesFromCache(entitySpecs, this.entityMap);

    let unresolvedEntitySpecs = entitySpecs.filter(v => !this.entityMap.has(v.entity));

    let unresolvedLovNames = entitySpecs.filter(v => v.lov).filter(v => {
      let entityObject = this.entityMap.get(v.entity);
      return (!entityObject || entityObject.lovItems == undefined);
    }).map(v => v.entity);


    let promiseArray = [(unresolvedEntitySpecs.length) ? this.fetchMissingEntities(unresolvedEntitySpecs.map(s => s.entity)) : [],
      (unresolvedLovNames.length) ? this.fetchMissingEntityLovs(unresolvedLovNames) : {}];

    return Promise.all(promiseArray)
      .then( ([entityArray, lovObject]: [ MdEntity[], {[key: string]: LovItem[]} ]) => {
        const lang = loc.lang;

        for(let entity of entityArray) {
          let lovItems = lovObject[entity.id];
          if (lovItems != undefined) {
            entity.lovItems = lovItems;
            delete lovObject[entity.id];
          }

          this.entityMap.set(entity.id, entity);
          setItemToLocalStorage("MdEntity", lang, entity.id, entity);
        }

        for(let entityName in lovObject) {
          let entityObject = this.entityMap.get(entityName);
          entityObject.lovItems = lovObject[entityName];
          this.entityMap.set(entityName, entityObject);
          setItemToLocalStorage("MdEntity", lang, entityName, entityObject);
        }
        return this.entityMap;
    });
  }


  private fetchMissingEntities(entityNames: string[]) : Promise<MdEntity[]> {
    return mdAxios.get('/core/metamodel/entity', {params: {entityName: entityNames}})
      .then((response) => response.data.map(v => new MdEntity(v))) ;
  }

  fetchMissingEntityLovs(entityNames: string[], tenantIds: number[] = undefined): Promise<{[key: string]: LovItem[]}> {
    return mdAxios.get('/core/metamodel/lovitem', {params: {entityName: entityNames, tenantIds}})
      .then((response) => response.data);
  }



  /**
   *
   * @param mainEntityNames - pole jmen hlavnich entit, ktere dotahneme bez lov a k nim potom dotahneme entity vsech fieldu s localValueSource vcetne lov
   * @param entityNamesOrSpecs - pole jmen entit ktere dotahneme bez lovs, nebo pole objektu [{entity: 'Country', lov: true}, ...]
   */
  fetchEntityMetadata(mainEntityNames: string[], entityNamesOrSpecs?: EntityNameOrSpec[]): Promise<ObservableMap<MdEntity>> {

    entityNamesOrSpecs = (entityNamesOrSpecs) ? entityNamesOrSpecs : [];
    let entitySpecs = entityNamesOrSpecs.map(v => (typeof v === 'string') ? {entity: v} : v);

    return this.fetchEntities(mainEntityNames)
      .then(entityMap => {

        let allFields = flatten(mainEntityNames.map(entityName => entityMap.get(entityName).getFields()));
        let fieldSpecs: EntitySpec[] = allFields.filter(f => f.hasLocalValueSource()).map(f => ({entity: f.valueSource, lov: true}));
        let allEntitySpecs = fieldSpecs.concat(entitySpecs);

        let objectifiedSpecs: {[key: string]: EntitySpec} = allEntitySpecs.reduce( (acc, spec) => {
          let old = acc[spec.entity];
          if (old) {
            old.lov = old.lov || spec.lov;
          } else {
            acc[spec.entity] = spec;
          }
          return acc;
        }, {});

        return this.fetchEntities(Object.values(objectifiedSpecs));
      });
  }



  private fetchEntitiesFromCache(entitySpecs: EntitySpec[], entityMap: ObservableMap<MdEntity>): void {
    const lang = loc.lang;

    for(let entitySpec of entitySpecs) {
      if (entityMap.has(entitySpec.entity)) {
        continue;
      }
      let entityStr =  getItemFromLocalStorage("MdEntity", lang, entitySpec.entity);
      if (entityStr) {
        let entity = new MdEntity(JSON.parse(entityStr));
        entityMap.set(entity.id, entity);
      }
    }
  }


  fetchNextEntityNumber(entityName: string, partyId: number, profitCenterId: number, invoiceTypeId: number): Promise<String> {
    return mdAxios.post('/core/meta/next-entity-number', {entityName, partyId, profitCenterId, invoiceTypeId})
      .then((response) => response.data.entityNumber);
  }


  getLovItem(entityName: string, id: ID): Promise<LovItem> {
    return mdAxios.get("/core/meta/search/" + entityName + "/" + id)
      .then((response) => response.data);
  }


  spelExpression(expression: string, additionalParams: object = {}): any {
    let struct = Object.assign({}, additionalParams, {expression});
    return mdAxios.post("/core/meta/spel-expression", struct, {showPrettyError: true})
      .then((response) => lovValue(response.data));
  }





}

const mdEntityService = new MdEntityService();
export default mdEntityService;



// WEBPACK FOOTER //
// ./src/core/metamodel/mdEntityService.ts